import { Button, Heading, Input, LuIcon, Separator, Spinner, Text, toast } from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError } from "@overmap-ai/core"
import { useForm } from "@tanstack/react-form"
import { useNavigate } from "@tanstack/react-router"
import { type FormEventHandler, memo, useCallback } from "react"

import { MapBackgroundContainer } from "../../components/MapBackgroundContainer/MapBackgroundContainer"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"

export const CreateOrganizationView = memo(() => {
	const navigate = useNavigate()

	const form = useForm({
		defaultValues: {
			name: "",
		},
		onSubmit: async ({ value, formApi }) => {
			try {
				const organization = await sdk.organizations.add({ name: value.name })

				toast.success({ title: "Welcome!", description: "" })

				void navigate({
					from: "/organizations",
					to: "/$organizationId",
					params: {
						organizationId: organization.uuid,
					},
				})

				formApi.reset()
			} catch (error) {
				formApi.setErrorMap({
					onSubmit: {
						form: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
						fields: {},
					},
				})
			}
		},
	})

	const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()
			void form.handleSubmit()
		},
		[form],
	)

	return (
		<MapBackgroundContainer>
			<div className="items-cetner flex size-full flex-col justify-center gap-2 p-4">
				<Heading size="lg">Create your organization</Heading>

				<Separator size="full" />

				<form className="flex w-full flex-col items-center gap-2" onSubmit={handleSubmit}>
					<form.Field
						name="name"
						validators={{
							onBlur: ({ value }) => (!value ? "Name is required" : undefined),
						}}
					>
						{(field) => (
							<div className="flex w-full flex-col gap-1">
								<label htmlFor={field.name}>
									<Text>Name</Text>
								</label>

								<Input.Root
									variant="outline"
									className="!bg-(--base-a2) w-full"
									accentColor={!field.state.meta.isValid ? "danger" : "primary"}
								>
									<Input.Field
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="ex. acmeinc"
									/>
								</Input.Root>

								{!field.state.meta.isValid && (
									<Text size="sm" weight="regular" accentColor="danger">
										{field.state.meta.errors.join(",")}
									</Text>
								)}
							</div>
						)}
					</form.Field>

					<form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
						{([canSubmit, isSubmitting]) => (
							<Button className="my-2 w-full" type="submit" disabled={!canSubmit} variant="surface">
								{isSubmitting ? (
									<Spinner />
								) : (
									<>
										<LuIcon icon={modelIconMapping.add} />
										Create
									</>
								)}
							</Button>
						)}
					</form.Subscribe>

					<form.Subscribe selector={(state) => [state.errorMap]}>
						{([errorMap]) =>
							!!errorMap.onSubmit && (
								<Text accentColor="danger" size="xs">
									{errorMap.onSubmit}
								</Text>
							)
						}
					</form.Subscribe>
				</form>
			</div>
		</MapBackgroundContainer>
	)
})
CreateOrganizationView.displayName = "CreateOrganizationView"
